import Head from 'next/head'
import { Breadcrumb } from '@/commons/Breadcrumb'
import { PersonalTrainerHead } from '../../../components/index/service-category/personal-trainer/PersonalTrainerHead'
import { openModal } from 'service/modal-service'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerBooking } from '../../../components/index/service-category/personal-trainer/PersonalTrainerBooking'
import Script from 'next/script'
import { useState } from 'react'
import { contentDate } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { s3Static } from '../../../utils/constants'
import { PersonalTrainerStructure } from '@/index/service-category/personal-trainer/PersonalTrainerStructure'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const countryName = "Singapore"

    const allRequestCategorySettings = await getRequestJSON(countryName)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

  return {
      props: {
        requestCategorySettings,
        countryName,
        modifiedDate,
        month,
        year
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function WhyChoose(props) {
    const requestCategorySettings = props.requestCategorySettings
    const modifiedDate = props.modifiedDate
    const year = props.year

    const [requestForm, setRequestForm] = useState(true)

    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

  return (
    <>
      <Head>
        <title>Why Choose StringsSG Personal Training - Certified Trainers & Proven Results</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description"
            content="Discover why StringsSG is Singapore's top personal training platform. Certified trainers, comprehensive training structure, free consultations, and proven results since 2018."/>
        <meta name="keywords"
            content="why choose personal trainer singapore, certified personal trainer, personal training structure, fitness training program, stringssg personal training"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/personal-trainer/why-choose`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content="Why Choose StringsSG Personal Training - Certified Trainers & Proven Results"/>
        <meta property="og:description" content="Discover why StringsSG is Singapore's top personal training platform. Certified trainers, comprehensive training structure, free consultations, and proven results since 2018."/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-06T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/personal-trainer/why-choose`}/>
        <meta name="twitter:title" content="Why Choose StringsSG Personal Training - Certified Trainers & Proven Results"/>
        <meta name="twitter:description" content="Discover why StringsSG is Singapore's top personal training platform. Certified trainers, comprehensive training structure, free trials, and proven results since 2018."/>
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/why-choose" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Why Choose StringsSG Personal Training",
              "description": "Discover why StringsSG is Singapore's top personal training platform. Certified trainers, comprehensive training structure, free consultations, and proven results since 2018.",
              "provider": {
                "@type": "Organization",
                "name": "StringsSG",
                "url": "https://www.stringssg.com",
                "sameAs": ["https://www.facebook.com/StringsSG/"]
              },
              "areaServed": "Singapore",
              "url": "https://www.stringssg.com/sg/personal-trainer/why-choose"
            })
          }}
        />
      </Head>
        <div className="">
            <RequestProvider>
                <PersonalTrainerHead
                    breadcrumb={<Breadcrumb jsonLd={false} pageTitle="Why Choose StringsSG" dark />}
                    heroImage={s3Static + "personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"}
                    title="Why Choose StringsSG Personal Training"
                    heading="Why Choose StringsSG Personal Training"
                    heroCopy="Directly employed, certified trainers. Transparent pricing from $84/session, free consultation, no lock-in."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />
            </RequestProvider>

            <header className="text-center py-8 px-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose StringsSG Personal Training</h2>
                <p className="text-gray-600 max-w-3xl mx-auto">Discover why StringsSG is Singapore&apos;s top personal training platform with certified trainers, comprehensive training structure, free consultations, and proven results since 2018.</p>
            </header>

            <PersonalTrainerStructure />

            {/* Why Choose StringsSG Section */}
            <PersonalTrainerWhyUs />

            <RequestProvider>
                <PersonalTrainerBooking
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    year={year}
                />
            </RequestProvider>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
